import React, { useState, useRef, useEffect } from "react";
import {
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useDispatch, useSelector } from "react-redux";
import { chatActions } from "store/chat";
import { db, rtdb } from "firebaseApp";
import { collection, doc, setDoc, updateDoc } from "firebase/firestore";
import ContextSizeSelector from "components/chat/ContextSizeSelector";
import { useAuth } from "context/react-lib/firebase-auth/AuthProvider";
import AllertMessage from "components/react-lib/generic-ui/AlertMessage";
import { createConversation } from "utils/conversations";
import RecordButton from "components/react-lib/generic-ui/RecordButton";
import ClearIcon from "@mui/icons-material/Clear";
import { COLLECTIONS } from "firebaseCollections";
import AttachmentList from "components/files/AttachmentList";
import AttachFileIconButton from "components/attachments/AttachFileIconButton";
import { useTheme } from "@emotion/react";
import useIsMobile from "hooks/react-lib/generic-ui/useIsMobile";
import ImageRatioSelector from "components/chat/ImageRatioSelector";
import { ref, update } from "firebase/database";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useDocumentData } from "react-firebase-hooks/firestore";

export default function ChatInput({ ...props }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const auth = useAuth();
  const uid = auth.currentUser.uid;
  const isMobile = useIsMobile();
  const params = useParams();

  const theme = useTheme();

  const botError = useSelector((state) => state.chat.botError);
  const botState = useSelector((state) => state.bot);
  const botId = botState.current;
  const bot = botState.idMap[botId];
  const conversationId = params.conversationId;

  const hasAttachments = useSelector((state) => state.chat.hasAttachments);

  const [conversation, loadingConversation, conversationError] = useDocumentData(conversationId ? 
    doc(db, "conversations", conversationId)
    : null
  )

  const prompt = useSelector((state) => state.chat.prompt);
  // const audioState = useSelector((state) => state.chat.botAudio[botId]) || {
  //   recording: false,
  //   audioUrl: "",
  // };
  const audioState = useSelector((state) => state.chat.audio);

  const [shiftOn, setShiftOn] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [messageId, setMessageId] = useState();
  const [answerId, setAnswerId] = useState();
  const [cancelling, setCancelling] = useState(false);
  const [isComposing, setIsComposing] = useState(false);

  const localStream = useSelector((state) => state.chat.localStream);

  const listening = useSelector((state) => state.chat.listening);

  const handleStartAudioRecording = () => {
    dispatch(
      chatActions.setBotAudio({
        botId: bot.id,
        audioUrl: "",
        recording: true,
      })
    );
  };

  const setAudioUrl = (audioUrl) => {
    dispatch(
      // chatActions.setBotAudio({
      //   botId: bot.id,
      //   audioUrl,
      //   recording: false,
      // })
      chatActions.setAudio({
        audioUrl,
        recording: false,
      })
    );
  };

  const convertBlobURLToBase64 = async (blobURL) => {
    try {
      const response = await fetch(blobURL);
      if (!response.ok) {
        throw new Error("Failed to fetch blob data");
      }
      const blob = await response.blob();
      return await convertBlobToBase64(blob);
    } catch (error) {
      console.error("Error converting blob URL to Base64:", error);
      throw error;
    }
  };

  const convertBlobToBase64 = async (blob) => {
    try {
      const reader = new FileReader();
      const result = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      return result;
    } catch (error) {
      console.error("Error converting blob to Base64:", error);
      throw error;
    }
  };

  const handleSendMessage = async () => {
    dispatch(chatActions.clearBotError(botId));
    dispatch(chatActions.setTyping(false));
    // dispatch(chatActions.setBotProcessing(true));
    setProcessing(true);
    let messageRef;
    try {
      let conversationId = params.conversationId;
      if (!conversationId) {
        const conversation = await createConversation(bot.id, uid, dispatch);
        conversationId = conversation.id;
        navigate(`/${conversationId}`);
      }
      const messagesRef = collection(db, COLLECTIONS.messages);
      const userMessageRef = doc(messagesRef);
      const answerMessageRef = doc(messagesRef);
      const userMessage = {
        uid,
        botId,
        audioUrl: audioState.audioUrl,
        conversationId,
        content: prompt,
        createdAt: new Date().getTime(),
        role: "user",
        type: audioState.audioUrl ? "audio" : "text",
        bookmarked: false,
        cancelled: false,
        contextSize: botState.contextSize,
        answerMessageId: answerMessageRef.id,
      };
      if (audioState.audioUrl) {
        userMessage.content = "";
        userMessage.audioBase64 = await convertBlobURLToBase64(
          audioState.audioUrl
        );
      }
      const answerMessage = {
        role: "assistant",
        content: "...",
        streaming: true,
        processing: true,
        bookmarked: false,
        cancelled: false,
        botId,
        conversationId,
        uid,
        userMessageId: userMessageRef.id,
        createdAt: new Date().getTime() + 1,
      };
      await Promise.all([
        updateDoc(doc(db, "conversations", conversationId), {
          processing: true,
        }),
        setDoc(userMessageRef, userMessage),
        setDoc(answerMessageRef, answerMessage),
      ]);
      // const sessionRef = ref(rtdb, `streams/${conversationId}`);
      // const streamData = { text: prompt, botId: bot.id, status: "started" };
      // dispatch(chatActions.setLocalStream(streamData));
      // await set(sessionRef, streamData);
      dispatch(chatActions.reset());
    } catch (error) {
      console.log(error);
      dispatch(
        chatActions.setBotError({
          error: "An error occurred. Try again later.",
          botId,
        })
      );
      // dispatch(chatActions.setLocalStream(null));
    } finally {
      dispatch(
        chatActions.setBotProcessing({ botId: bot.id, processing: false })
      );
      setProcessing(false);
    }
  };

  // useEffect(() => {
  //   if (
  //     localStream &&
  //     !localStream.cancelled &&
  //     localStream.status === "done"
  //   ) {
  //     dispatch(chatActions.reset());
  //   }
  // }, [localStream]);

  const disabled = () => {
    if (shiftOn) return true;
    if (!bot) return true;
    if (processing) return true;
    if (listening) return true;
    if (conversation && conversation.processing) return true;
    // if (localStream && !localStream.cancelled) return true;
    return (
      !Boolean(prompt) &&
      !Boolean(audioState?.audioUrl) &&
      (!hasAttachments || !bot.systemPrompt)
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "Shift") {
      setShiftOn(true);
    }
    if (disabled()) return;
    // Only send the message if composition is not active
    if (event.key === "Enter" && !event.shiftKey && !isComposing) {
      if (!isMobile) {
        event.preventDefault();
        handleSendMessage();
      }
    }
  };

  const handleOnFocus = () => {
    dispatch(chatActions.setTyping(true));
  };

  const handleOnBlur = () => {
    dispatch(chatActions.setTyping(false));
  };

  const handleCancel = async () => {
    try {
      const sessionRef = ref(rtdb, `streams/${conversationId}`);
      const streamData = { cancelled: true };
      dispatch(chatActions.setLocalStream(streamData));
      await update(sessionRef, streamData);
    } catch (error) {
      console.log(error);
      toast.error("Error cancelling message");
    }
  };

  const handleKeyUp = (event) => {
    if (event.key === "Shift") {
      setShiftOn(false);
    }
  };

  const renderSendButton = () => {
    // if (
    //   processing ||
    //   listening ||
    //   (localStream && !localStream.cancelled && localStream.status !== "done")
    // ) {
    //   const cancelDisabled = cancelling;
    //   return (
    //     <IconButton onClick={handleCancel} disabled={cancelDisabled}>
    //       <StopCircleIcon
    //         sx={{ color: colors.white, opacity: cancelDisabled ? 0.25 : 1 }}
    //       />
    //     </IconButton>
    //   );
    // } else {
    const isDisabled = disabled();
    return (
      <Stack alignItems="center">
        <IconButton
          color="secondary"
          onClick={handleSendMessage}
          disabled={isDisabled}
          data-testid="send-message-button"
        >
          <SendIcon />
        </IconButton>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.main,
          }}
        >
          {audioState.audioUrl ? "audio" : prompt ? "text" : ""}
        </Typography>
      </Stack>
    );
    // }
  };

  const handlePromptChange = (newPrompt) => {
    dispatch(chatActions.clearBotError());
    dispatch(chatActions.setPrompt(newPrompt));
  };

  const handleClearRecording = () => {
    dispatch(
      chatActions.setBotAudio({
        botId: bot.id,
        audioUrl: "",
        recording: false,
      })
    );
  };

  return (
    <Stack spacing={1} width="100%" maxWidth="748px">
      {botError && botError[botId] && (
        <AllertMessage severity="error" data-testid="chat-error-message">
          {botError[botId]}
        </AllertMessage>
      )}
      <AttachmentList label="Bot attachments" targetId={botId} />
      <AttachmentList
        label="Conversation attachments"
        targetId={conversationId}
      />
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row">
          <RecordButton
            startRecordingCallback={handleStartAudioRecording}
            clearRecordingCallback={handleClearRecording}
            audioURL={audioState.audioUrl}
            setAudioURL={setAudioUrl}
          />
          <AttachFileIconButton
            buttonProps={{
              color: "text",
            }}
          />
          <ImageRatioSelector />
        </Stack>
        <Stack direction="row">
          {prompt && (
            <IconButton onClick={() => handlePromptChange("")}>
              <ClearIcon sx={{ color: theme.palette.text.main }} />
            </IconButton>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ContextSizeSelector color="secondary" />
        <TextField
          {...props}
          data-testid="chat-input"
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          disabled={
            processing ||
            listening ||
            audioState?.recording ||
            // (localStream &&
            //   !localStream.cancelled &&
            //   !localStream.status !== "done") ||
            Boolean(audioState?.audioUrl)
          }
          value={prompt}
          onInput={(event) => handlePromptChange(event.target.value)}
          multiline
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          maxRows={6}
          InputProps={{
            style: {
              backgroundColor: theme.palette.inputBackground.main,
              color: theme.palette.text.main,
            },
          }}
        />
        {renderSendButton()}
      </Stack>
      <LinearProgress
        color="secondary"
        sx={{
          opacity:
            localStream &&
            !localStream.cancelled &&
            localStream.status !== "done"
              ? 0.5
              : 0,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.main,
        }}
        textAlign="center"
      >
        AI can make mistakes. Check important information.
      </Typography>
    </Stack>
  );
}
